/**
 * ÁREA CLIENTE — INDICADORES DE MANTENIMIENTO (FASE 12.4)
 *
 * Cómo va el mantenimiento de SUS centros, con datos públicos: trabajos en marcha, incidencias abiertas, próximos
 * preventivos, cumplimiento de los preventivos, tiempo de resolución, evolución, centros, ubicaciones y equipos.
 * Nunca técnicos, carga interna, partes sin técnico, motivos internos ni nada de otro cliente: lo decide el
 * servidor (`/api/portal/mantenimiento/indicadores`), con la misma definición que los indicadores de gestión.
 * Cada cifra lleva a SUS partes con ese filtro; el periodo y el centro viven en la URL.
 */
import { useId, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { api } from '../api.ts';
import { Cargando, Error as ErrorCaja, fecha, hoyLocal, useDatos } from '../componentes/base.tsx';
import { textoCociente, textoHoras, useDrillMnt } from '../componentes/indicadores-mnt.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useListaUrl } from '../listaUrl.ts';

interface Cociente { pct: number | null; n: number; total: number }
interface IndicadoresPublicos {
  periodo: { desde: string; hasta: string };
  ahora: { partesActivos: number; incidenciasAbiertas: number; proximosPreventivos: number };
  periodoActividad: { creados: number; cerrados: number; creadosAnterior: number };
  resolucion: { mediaHoras: number | null; medianaHoras: number | null; n: number };
  preventivos: { previstos: number; ejecutados: number; vencidos: number; porLlegar: number; cumplimiento: Cociente };
  evolucion: { mes: string; creados: number; cerrados: number; preventivosEjecutados: number }[];
  porCentro: { idCentro: string; centro: string; creados: number; cerrados: number; activos: number }[];
  porUbicacion: { idCentro: string; centro: string; idUbicacion: string | null; ubicacion: string; total: number; activos: number; cerrados: number; correctivos: number; preventivos: number }[];
  activos: { id: string; codigo: string; nombre: string; incidencias: number; intervenciones: number }[];
}

const RAIZ = '/portal/mantenimiento';
const SIN_DATOS = 'No hay datos en este periodo.';
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function Cifra({ titulo, valor, detalle, a, estado }: { titulo: string; valor: string; detalle?: string; a?: string; estado?: unknown }): ReactNode {
  const cuerpo = (
    <>
      <div className="etiqueta-dato">{titulo}</div>
      <div className="cifra" style={{ fontSize: '1.6rem', fontWeight: 600 }}>{valor}</div>
      {detalle !== undefined && <div className="pequena tenue">{detalle}</div>}
    </>
  );
  return (
    <div className="panel kpi-tarjeta" style={{ flex: '1 1 160px', margin: 0 }}>
      <div className="panel-cuerpo">{a === undefined ? cuerpo : <Link to={a} state={estado} className="kpi-enlace" aria-label={`${titulo}: ${valor}. Ver el listado`}>{cuerpo}</Link>}</div>
    </div>
  );
}

export function PaginaPortalIndicadoresMnt(): ReactNode {
  const idBase = useId();
  const l = useLocation();
  const lista = useListaUrl({ periodo: '90', idCentro: '' });
  const periodo = lista.valor('periodo');
  const idCentro = lista.valor('idCentro');
  const hoy = hoyLocal();
  const desde = periodo === 'anio' ? `${hoy.slice(0, 4)}-01-01` : hoyLocal(periodo === '30' ? -29 : -89);
  const q = new URLSearchParams({ desde, hasta: hoy });
  if (idCentro !== '') q.set('idCentro', idCentro);
  const consulta = q.toString();
  const centros = useDatos(() => api.get<{ filas: { id: string; nombre: string }[] }>('/api/portal/mantenimiento/centros'), []);
  const datos = useDatos(() => api.get<IndicadoresPublicos>(`/api/portal/mantenimiento/indicadores?${consulta}`), [consulta]);
  const drill = useDrillMnt();
  const per = { pDesde: desde, pHasta: hoy };
  const partes = (f: Record<string, string>): string => drill(`${RAIZ}/partes`, { centro: idCentro, ...f });
  const volverAqui = { volver: { ruta: `${l.pathname}${l.search}`, texto: 'Indicadores' } };

  return (
    <Shell migas={[{ texto: 'Mantenimiento', a: RAIZ }, { texto: 'Indicadores' }]} titulo="Indicadores de mantenimiento">
      <div className="panel">
        <div className="panel-cuerpo">
          <div className="fila filtros-calidad" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-p`}>Periodo</label>
              <select id={`${idBase}-p`} value={periodo} onChange={(e) => lista.fijar('periodo', e.target.value)}>
                <option value="30">Últimos 30 días</option>
                <option value="90">Últimos 90 días</option>
                <option value="anio">Año actual</option>
              </select>
            </div>
            {(centros.datos?.filas.length ?? 0) > 1 && (
              <div className="campo" style={{ margin: 0 }}>
                <label htmlFor={`${idBase}-c`}>Centro</label>
                <select id={`${idBase}-c`} value={idCentro} onChange={(e) => lista.fijar('idCentro', e.target.value)}>
                  <option value="">Todos</option>
                  {(centros.datos?.filas ?? []).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
            )}
          </div>
          <p className="pequena tenue" style={{ marginBottom: 0 }}>Del {fecha(desde)} al {fecha(hoy)}. «Ahora» es la situación de hoy; el resto, lo ocurrido en el periodo.</p>
        </div>
      </div>

      {datos.cargando && datos.datos === null && <Cargando texto="Calculando indicadores…" />}
      {datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}
      {datos.datos !== null && (() => {
        const d = datos.datos;
        const p = d.preventivos;
        return (
          <>
            <section aria-labelledby="pm-ahora" className="bloque-indicadores">
              <h2 id="pm-ahora" className="titulo-bloque">Ahora</h2>
              <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Cifra titulo="Trabajos en marcha" valor={String(d.ahora.partesActivos)} detalle="partes de trabajo abiertos o en curso" a={partes({ indicador: 'ACTIVOS' })} />
                <Cifra titulo="Incidencias abiertas" valor={String(d.ahora.incidenciasAbiertas)} detalle="las que comunicaste y siguen abiertas" a={`${RAIZ}/incidencias`} estado={volverAqui} />
                <Cifra titulo="Próximos preventivos" valor={String(d.ahora.proximosPreventivos)} detalle="mantenimientos ya programados" a={`${RAIZ}/proximos`} />
              </div>
            </section>

            <section aria-labelledby="pm-preventivos" className="bloque-indicadores">
              <h2 id="pm-preventivos" className="titulo-bloque">Mantenimiento preventivo</h2>
              <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Cifra titulo="Cumplimiento" valor={textoCociente(p.cumplimiento)}
                  detalle={p.cumplimiento.pct === null ? 'todavía no hay preventivos cuya fecha haya pasado' : `${String(p.cumplimiento.n)} hechos de ${String(p.cumplimiento.total)} que ya tocaban`} />
                <Cifra titulo="Hechos" valor={String(p.ejecutados)} detalle={`de ${String(p.previstos)} previstos en el periodo`} a={partes({ indicador: 'PREV_EJECUTADOS', ...per })} />
                <Cifra titulo="Pendientes de hacer" valor={String(p.vencidos)} detalle="su fecha ya pasó" a={partes({ indicador: 'PREV_VENCIDOS', ...per })} />
                <Cifra titulo="Por llegar" valor={String(p.porLlegar)} detalle="todavía no toca" a={partes({ indicador: 'PREV_POR_LLEGAR', ...per })} />
              </div>
            </section>

            <section aria-labelledby="pm-evolucion" className="bloque-indicadores">
              <h2 id="pm-evolucion" className="titulo-bloque">Evolución</h2>
              <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Cifra titulo="Partes abiertos" valor={String(d.periodoActividad.creados)} detalle={`${String(d.periodoActividad.creadosAnterior)} en el periodo anterior`} a={partes({ indicador: 'CREADOS', ...per })} />
                <Cifra titulo="Partes cerrados" valor={String(d.periodoActividad.cerrados)} a={partes({ indicador: 'CERRADOS', ...per })} />
                <Cifra titulo="Tiempo medio de resolución" valor={textoHoras(d.resolucion.mediaHoras)}
                  detalle={d.resolucion.n === 0 ? SIN_DATOS : `desde que se abre hasta que se cierra · mediana ${textoHoras(d.resolucion.medianaHoras)}`} />
              </div>
              <div className="panel" style={{ marginTop: 12 }}>
                <div className="panel-cuerpo">
                  {!d.evolucion.some((m) => m.creados + m.cerrados + m.preventivosEjecutados > 0) ? <p className="pequena tenue">{SIN_DATOS}</p> : (
                    <div className="tabla-desliza">
                      <table className="tabla-tarjetas tabla-evolucion">
                        <caption className="sr-solo">Evolución por mes</caption>
                        <thead><tr><th>Mes</th><th>Abiertos</th><th>Cerrados</th><th>Preventivos hechos</th></tr></thead>
                        <tbody>
                          {d.evolucion.map((m) => (
                            <tr key={m.mes}>
                              <td className="celda-titulo">{`${MESES[Number(m.mes.slice(5, 7)) - 1] ?? ''} ${m.mes.slice(0, 4)}`}</td>
                              <td data-label="Abiertos">{m.creados}</td>
                              <td data-label="Cerrados">{m.cerrados}</td>
                              <td data-label="Preventivos hechos">{m.preventivosEjecutados}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section aria-labelledby="pm-donde" className="bloque-indicadores">
              <h2 id="pm-donde" className="titulo-bloque">Centros y ubicaciones</h2>
              {d.porCentro.length > 1 && (
                <div className="panel">
                  <div className="panel-cabecera"><h3>Por centro</h3></div>
                  <div className="panel-cuerpo">
                    <div className="tabla-desliza">
                      <table className="tabla-tarjetas kpi-tabla" aria-label="Partes por centro">
                        <thead><tr><th>Centro</th><th>Abiertos</th><th>Cerrados</th><th>En marcha ahora</th></tr></thead>
                        <tbody>
                          {d.porCentro.map((c) => (
                            <tr key={c.idCentro}>
                              <td className="celda-titulo">{c.centro}</td>
                              <td data-label="Abiertos"><Link to={drill(`${RAIZ}/partes`, { centro: c.idCentro, indicador: 'CREADOS', ...per })}>{c.creados}</Link></td>
                              <td data-label="Cerrados"><Link to={drill(`${RAIZ}/partes`, { centro: c.idCentro, indicador: 'CERRADOS', ...per })}>{c.cerrados}</Link></td>
                              <td data-label="En marcha ahora"><Link to={drill(`${RAIZ}/partes`, { centro: c.idCentro, indicador: 'ACTIVOS' })}>{c.activos}</Link></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              <div className="panel">
                <div className="panel-cabecera"><h3>Por ubicación</h3></div>
                <div className="panel-cuerpo">
                  <p className="pequena tenue" style={{ marginTop: 0 }}>Partes abiertos en el periodo, por dónde estaba el equipo cuando se abrió el parte.</p>
                  {d.porUbicacion.length === 0 ? <p className="pequena tenue">{SIN_DATOS}</p> : (
                    <div className="tabla-desliza">
                      <table className="tabla-tarjetas kpi-tabla" aria-label="Partes por ubicación">
                        <thead><tr><th>Ubicación</th><th>Centro</th><th>Partes</th><th>En marcha</th><th>Cerrados</th><th>Reparaciones</th><th>Preventivos</th></tr></thead>
                        <tbody>
                          {d.porUbicacion.map((u) => (
                            <tr key={`${u.idCentro}-${u.idUbicacion ?? 'sin'}`}>
                              <td className="celda-titulo">
                                <Link to={drill(`${RAIZ}/partes`, { centro: u.idCentro, indicador: 'CREADOS', ...per, idUbicacion: u.idUbicacion ?? 'SIN', ubicacion: u.ubicacion })}>{u.ubicacion}</Link>
                              </td>
                              <td data-label="Centro">{u.centro}</td>
                              <td data-label="Partes">{u.total}</td>
                              <td data-label="En marcha">{u.activos}</td>
                              <td data-label="Cerrados">{u.cerrados}</td>
                              <td data-label="Reparaciones">{u.correctivos}</td>
                              <td data-label="Preventivos">{u.preventivos}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section aria-labelledby="pm-activos" className="bloque-indicadores">
              <h2 id="pm-activos" className="titulo-bloque">Equipos con más intervenciones</h2>
              <div className="panel">
                <div className="panel-cuerpo">
                  {d.activos.length === 0 ? <p className="pequena tenue">{SIN_DATOS}</p> : (
                    <ul className="lista-kpi" aria-label="Equipos con más intervenciones">
                      {d.activos.map((a) => (
                        <li key={a.id}>
                          <span><Link to={`${RAIZ}/activos/${a.id}`} state={volverAqui}>{a.nombre}</Link> <span className="pequena tenue mono">{a.codigo}</span></span>
                          <span className="pequena">
                            <Link to={drill(`${RAIZ}/partes`, { activo: a.id, activoNombre: a.nombre, indicador: 'CREADOS', ...per })}>{a.intervenciones} {a.intervenciones === 1 ? 'parte' : 'partes'}</Link>
                            {' · '}{a.incidencias} {a.incidencias === 1 ? 'incidencia' : 'incidencias'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </section>
          </>
        );
      })()}
    </Shell>
  );
}
